import { FC, useEffect, useMemo, useRef, useState } from "react";
import type { CachedTopic } from "@/types/intent";
import { IntentTopicSelector } from "@/components/filters/IntentTopicSelector";
import { useGetIntentTopicsByIdsLazyQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";

interface Props {
  selectedTopicIds: string[];
  onSelect: (topicIds: string[]) => void;
}

export const HydratedIntentTopicSelector: FC<Props> = ({
  selectedTopicIds,
  onSelect,
}) => {
  const [topicsCache, setTopicsCache] = useState<CachedTopic[]>([]);
  const inFlightIdsRef = useRef<Set<string>>(new Set());
  const requestRef = useRef<string | null>(null);

  const [fetchTopicsByIds] = useGetIntentTopicsByIdsLazyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const cacheById = useMemo(
    () => new Map(topicsCache.map((t) => [t.id, t])),
    [topicsCache]
  );

  useEffect(() => {
    if (selectedTopicIds.length === 0) {
      requestRef.current = null;
      inFlightIdsRef.current.clear();
      setTopicsCache([]);
      return;
    }

    const missingIds = selectedTopicIds.filter(
      (id) => !cacheById.has(id) && !inFlightIdsRef.current.has(id)
    );

    if (missingIds.length === 0) {
      setTopicsCache(
        selectedTopicIds
          .map((id) => cacheById.get(id))
          .filter((t): t is CachedTopic => !!t)
      );
      return;
    }

    missingIds.forEach((id) => inFlightIdsRef.current.add(id));

    const requestKey = missingIds.join(",");
    requestRef.current = requestKey;

    fetchTopicsByIds({ variables: { ids: missingIds } })
      .then((result) => {
        if (requestRef.current !== requestKey) return;

        const topicsFromApi =
          result.data?.intentTopic?.data?.filter(
            (topic): topic is NonNullable<typeof topic> =>
              topic !== null && topic.id !== null && topic.displayName !== null
          ) ?? [];

        if (topicsFromApi.length === 0) return;

        setTopicsCache((prev) => {
          const nextMap = new Map(prev.map((t) => [t.id, t]));
          topicsFromApi.forEach((topic) => {
            nextMap.set(topic.id as string, {
              id: topic.id as string,
              displayName: topic.displayName as string,
              category: topic.category ?? undefined,
              collectionTree: (topic.collectionTree ?? []).filter(
                (t): t is string => t !== null
              ),
            });
          });

          return selectedTopicIds
            .map((id) => nextMap.get(id))
            .filter((t): t is CachedTopic => !!t);
        });
      })
      .finally(() => {
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
        missingIds.forEach((id) => inFlightIdsRef.current.delete(id));
      });
  }, [cacheById, fetchTopicsByIds, selectedTopicIds]);

  return (
    <IntentTopicSelector
      selectedTopicIds={selectedTopicIds}
      topicsCache={topicsCache}
      onSelectionChange={(topicIds, nextCache) => {
        onSelect(topicIds);
        setTopicsCache(nextCache);
      }}
      showToolbarLabel={false}
      mx={0}
    />
  );
};
